import { defineStore } from "pinia";
import {
  BookStatusEntry,
  BottomTab,
  GammaMapLayerType,
  GDT,
  IBaseMap,
  IMapLayerGroup,
  LayerControl,
  MapDrawSelection,
  MapLayerGroup,
  MapPosition,
  OSMapSettings,
} from "@/types";
import mapboxgl from "mapbox-gl";
import {
  getBookLayerName,
  unwrapDrawFeature,
  updateGammaDataSourceData,
} from "@/util/helpers";
import union from "@turf/union";
import difference from "@turf/difference";
import {
  useGammaData,
  useGroupedRecords,
  useLayout,
  useOrgSettings,
} from "@/services";
import { $router } from "@/main";
import { pick, snakeCase } from "lodash-es";
import { Polygon } from "geojson";
import { updateUrlQueryParams } from "@/router";
import { addGammaDataSource } from "@/util/helpers";

interface GroupMutation {
  path?: string;
  key: "isVisible" | "opacity";
  value: any;
}

interface MapState {
  isMapLoading: boolean;
  baseMapIndex: number;
  layerGroups: MapLayerGroup[];
  layerControls: LayerControl[];
  dataBoundary: DataBoundary;
  activeDatasetBoundary: DataBoundaryGeometry | null;
  screenBoundary: DataBoundaryGeometry | null;
  mapPosition: MapPosition;
  editingMode: MapDrawSelection;
  circleRadius: number;
  isEditingCircle: boolean;
  isEditingCoordinates: boolean;
  isDrawMenuOpen: boolean;
  markerPosition: mapboxgl.LngLat | null;
  map: mapboxgl.Map | null;
  mapDraw: any;
  editLayer: {
    source: string;
    polygon: Polygon | null;
    isDrawModeOnly: boolean;
  } | null;
  isDrawingRiskGeom: boolean;
  isEditingRiskGeom: boolean;
  loadedBooks: Map<string, any>;
  onBoundsUpdated: Map<string, () => void>;
}

export type DataBoundary = GeoJSON.Feature<
  GeoJSON.Polygon | GeoJSON.MultiPolygon,
  GeoJSON.GeoJsonProperties
> | null;
export type DataBoundaryGeometry =
  | GeoJSON.Polygon
  | GeoJSON.MultiPolygon
  | null;

export const useMap = defineStore("map", {
  state: (): MapState => ({
    isMapLoading: true,
    baseMapIndex: -1,
    layerGroups: [],
    layerControls: [],
    dataBoundary: null,
    activeDatasetBoundary: null,
    screenBoundary: null,
    mapPosition: {
      center: [-2.1742, 53.2454],
      zoom: 6.25,
      pitch: 0,
      bearing: 0,
    },
    editingMode: {
      additive: true,
      mode: "simple_select",
    },
    circleRadius: 500,
    isEditingCircle: false,
    isEditingCoordinates: false,
    isDrawMenuOpen: false,
    markerPosition: null,
    map: null,
    mapDraw: null,
    editLayer: null,
    isDrawingRiskGeom: false,
    isEditingRiskGeom: false,
    loadedBooks: new Map<string, any>(),
    onBoundsUpdated: new Map<string, () => void>(),
  }),
  getters: {
    mapSettings(): OSMapSettings | undefined {
      return useOrgSettings().settings?.mapSettings;
    },
    baseMaps(): IBaseMap[] {
      return (this.mapSettings?.baseMaps || []).map((bm) => ({
        ...bm,
        layerHooks: bm.layerHooks || {
          "below-roads": "above-base",
          "above-roads": "above-base",
        },
      }));
    },
    baseMap(): IBaseMap {
      return (
        this.baseMaps[this.baseMapIndex] ||
        this.baseMaps.find((m) => m.isDefault) ||
        this.baseMaps[0]
      );
    },
    layerDefinitions(): mapboxgl.Layer[] {
      return this.mapSettings?.layers || [];
    },
    mapOptions(): Partial<mapboxgl.MapboxOptions> {
      return {
        ...(this.mapSettings?.mapOptions || {}),
        ...(this.mapPosition || {}),
      };
    },
    activeLayers(): LayerControl[] {
      return (this.layerControls as LayerControl[]).filter(
        (lc) => lc.isVisible
      );
    },
    hasDataBoundary(): boolean {
      return Boolean(this.dataBoundary);
    },
    isEditingLayer(): boolean {
      return !!this.editLayer;
    },
    editableLayerSets(): Set<string> {
      return new Set(
        this.layerControls
          .filter((x) => x.isEditable)
          .flatMap((x) => x.layerIds)
      );
    },
  },
  actions: {
    updateBoundaryPolygon(feature?: DataBoundary) {
      let dataBoundary = feature ? unwrapDrawFeature(feature) : null;
      if (this.dataBoundary && dataBoundary) {
        dataBoundary = (this.editingMode.additive ? union : difference)(
          this.dataBoundary as any,
          dataBoundary as any
        ) as DataBoundary;
      }
      this.dataBoundary = dataBoundary;

      if (this.dataBoundary) {
        useGammaData().fetchAccumulations();
      }
    },
    getLayerVisibility(layerId: string): boolean {
      return !!this.layerControls.find((x) => x.id === layerId)?.isVisible;
    },
    setLayerVisibility(layerId: string, isVisible: boolean) {
      const control = this.layerControls.find((x) => x.id === layerId);
      if (!control) return;
      control.isVisible = isVisible;
    },
    async loadBook(book: BookStatusEntry) {
      if (this.loadedBooks.has(book.id) || !this.map) return;

      const bookName = getBookLayerName(book);

      await this.addBookSource(bookName, book);
      this.addBookLayer(bookName, book);
      useGammaData().selectDataSource({ lcId: bookName });
    },
    unloadBook(book: BookStatusEntry) {
      const bookName = getBookLayerName(book);

      if (!this.loadedBooks.has(bookName)) return;
      else {
        this.loadedBooks.delete(bookName);
        this.loadedBooks = new Map(this.loadedBooks);
      }
      this.removeBookLayer(bookName);
      this.removeBookSource(bookName);
    },
    removeBookSource(name: string) {
      if (!this.map) return;
      this.onBoundsUpdated.delete(name);
      this.map.removeSource(name);
      useGammaData().removeDataSource(name);
    },
    async addBookSource(name: string, book: BookStatusEntry) {
      if (!this.map) return;
      const sourceSettings: any = {
        dataSetType: "Policies",
        type: "gammadata",
        dataRequest: {
          catalogUri: `{base}/bookdata/${book.access}/${book.userId}/${book.id}`,
          displayColumns: [],
          orderBys: [],
          groupBys: [],
          filter: [],
        },
        geometryField: "geom",
        minzoom: 12,
        maxzoom: 22,
        visibleColumns: [],
      };

      addGammaDataSource({
        name: name,
        source: sourceSettings,
        map: this.map,
        layers: [name],
      });

      await useGammaData().addDataSource({
        name: name,
        source: sourceSettings,
        layers: [name],
      });

      this.onBoundsUpdated.set(name, () => {
        if (!this.map) return;
        updateGammaDataSourceData({
          name: name,
          source: sourceSettings,
          map: this.map,
          layers: [name],
          mapBoundsPolygon:
            this.activeDatasetBoundary || this.screenBoundary || undefined,
          shouldLoad: true,
        });
      });
    },
    setBookLayerVisibility(book: BookStatusEntry, isVisible: boolean) {
      const bookName = getBookLayerName(book);
      this.setLayerVisibility(snakeCase(bookName), isVisible);
    },
    getBookLayerVisibility(book: BookStatusEntry): boolean {
      const bookName = getBookLayerName(book);
      return this.getLayerVisibility(snakeCase(bookName));
    },
    isBookLoaded(book: BookStatusEntry): boolean {
      return this.loadedBooks.has(getBookLayerName(book));
    },
    removeBookLayer(name: string) {
      this.map?.removeLayer(name);
      const bookLayerGroup = this.layerGroups.find((x) => x.name === "Books");

      if (!bookLayerGroup) return;

      bookLayerGroup.removeSubLayer(name);

      if (!bookLayerGroup.layers.length) {
        this.removeLayerGroup(bookLayerGroup.id);
      }
    },
    addBookLayer(name: string, book: BookStatusEntry) {
      if (!this.map) return;

      const layerSettings: mapboxgl.AnyLayer = {
        id: name,
        type: "circle",
        source: name,
        paint: {
          "circle-color": book.color ?? "#17A2B8",
          "circle-radius": 7,
          "circle-opacity": 1,
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
        },
        layout: {
          visibility: "visible",
        },
        minzoom: 12,
        maxzoom: 22,
      };
      this.map.addLayer(layerSettings);

      if (!this.layerGroups.some((x) => x.name === "Books")) {
        this.addLayerGroup(
          new MapLayerGroup({
            name: "Books",
            description: "",
            type: GammaMapLayerType.Group,
            layers: [],
            source: "Book",
          })
        );
      }

      this.layerGroups
        .find((x) => x.name === "Books")
        ?.addSubLayer({
          name: name,
          type: GammaMapLayerType.Style,
          description: "",
          layers: [name],
          source: name,
        } as any);

      this.loadedBooks = new Map(this.loadedBooks.set(name, layerSettings));
    },
    addOnBoundsUpdated(id: string, func: () => void) {
      this.onBoundsUpdated.set(id, func);
    },
    setMapPosition(position: Partial<MapPosition>) {
      this.mapPosition = {
        ...this.mapPosition,
        ...position,
      };
      if ($router.currentRoute.path === "/") {
        updateUrlQueryParams("mapPosition", {
          ...this.mapPosition,
          ...position,
        }).then();
      }
    },
    hideAllLayers() {
      this.layerGroups.forEach((x) => (x.isVisible = false));
    },
    loadLayerGroups(lgs: IMapLayerGroup[]) {
      this.layerControls = [];
      this.layerGroups = [];
      lgs.forEach((lg) => this.addLayerGroup(new MapLayerGroup(lg)));
    },
    addLayerControl(lc: LayerControl) {
      const index = this.layerControls.findIndex((l) => l.path === lc.path);
      if (index > -1) {
        this.layerControls.splice(index, 1, lc);
      } else {
        this.layerControls = [...this.layerControls, lc];
      }
    },
    removeLayerControl(lcid: string) {
      this.layerControls = this.layerControls.filter((x) => x.id !== lcid);
    },
    addLayerGroup(lg: MapLayerGroup) {
      const index = this.layerGroups.findIndex((l) => l.path === lg.path);
      if (index > -1) {
        this.layerGroups.splice(index, 1, lg);
      } else {
        this.layerGroups = [...this.layerGroups, lg];
      }
    },
    removeLayerGroup(lgid: string) {
      this.layerGroups = this.layerGroups.filter((x) => x.id !== lgid);
    },
    updateLayerGroup({ key, value, path }: GroupMutation) {
      const index = this.layerGroups.findIndex((g) => g.path === path);
      if (index > -1) {
        const group = this.layerGroups[index] as MapLayerGroup;
        group[key] = value as never;
        this.addLayerGroup(group);
      }
    },
    setEditingMode(val: Partial<MapDrawSelection>, isDrawingRiskGeom = false) {
      this.isDrawingRiskGeom = isDrawingRiskGeom;
      this.editingMode = pick({ ...this.editingMode, ...val }, [
        "additive",
        "mode",
      ]);
    },
    setIsEditingRiskGeom(isEditing: boolean) {
      this.isEditingRiskGeom = isEditing;
    },
    setEditLayer(
      source: string | null,
      options: {
        isFocusMode?: boolean;
        isDrawModeOnly?: boolean;
      }
    ) {
      const uiLayout = useLayout();
      if (!source) {
        this.editLayer = null;
        this.setEditingMode({ mode: "simple_select" });

        if (options.isFocusMode) {
          uiLayout.restoreDrawers();
        }
        return;
      }

      this.editLayer = {
        source: source,
        polygon: null,
        isDrawModeOnly: options.isDrawModeOnly || false,
      };

      uiLayout.isBottomDrawerOpen = true;
      uiLayout.setBottomActiveTab(BottomTab.Groups);

      if (options.isFocusMode) {
        uiLayout.hideDrawers();
      }
    },
    async addBoundary(groupId: number): Promise<void> {
      if (!this.editLayer?.polygon) {
        return;
      }
      // exclusive for group records for now
      await useGroupedRecords().groupsAdd({
        columnInserts: [
          {
            column: {
              columnName: "group_number",
            },
            value: groupId,
          },
          {
            column: {
              columnName: "geom",
            },
            value: {
              ...this.editLayer?.polygon,
              coordinates: [
                [
                  ...this.editLayer?.polygon.coordinates[0],
                  this.editLayer?.polygon.coordinates[0][0],
                ],
              ],
            },
          },
        ],
      });

      if (this.editLayer.source) {
        this.refreshLayerData(this.editLayer.source);
      }
    },
    clearEditLayerPolygon() {
      if (this.editLayer) {
        this.mapDraw.deleteAll();
        this.editLayer.polygon = null;
        this.setEditingMode(this.editingMode);
      }
    },
    async refreshLayerData(layer: string) {
      if (!this.map) {
        return;
      }

      const dataSources = this.mapSettings?.sources as GDT.GammaDataSourceRaw;
      const layerDataSource = dataSources[layer] as GDT.GammaDataSourceRaw;

      if (!layerDataSource || !layerDataSource.dataRequest) {
        return;
      }

      const sources = this.mapSettings?.sources as Record<
        string,
        GDT.GammaDataSourceRaw
      >;

      const layers =
        this.mapSettings?.layers
          .filter((l) => ("source" in l && l.source) === layer)
          .map((l) => l.id) || [];

      updateGammaDataSourceData({
        name: layer,
        source: sources[layer],
        map: this.map,
        layers,
        mapBoundsPolygon:
          this.activeDatasetBoundary || this.screenBoundary || undefined,
        shouldLoad: true,
      });
    },
  },
});
